import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiPhone, FiMail, FiClock, FiCheck, FiUser, FiMessageSquare } = FiIcons;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    goal: '',
    journey: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };
  
  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return value.trim() ? '' : 'Name is required';
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? '' : 'Please enter a valid email';
      case 'phone':
        const phoneRegex = /^(\+91|91)?[6789]\d{9}$/;
        return phoneRegex.test(value) ? '' : 'Please enter a valid Indian phone number';
      case 'goal':
        return value ? '' : 'Please select a goal';
      case 'journey':
        return value.trim() ? '' : 'Please tell us about your journey';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Submit form (mock)
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    
    // In a real app, you would send the data to your backend here
    // For now, we'll just show a success message
    setTimeout(() => {
      // Redirect to body analysis page
      window.location.href = '/#/body-analysis';
    }, 2000);
  };

  const contactOptions = [
    {
      icon: FiMapPin,
      title: "Visit Our Center",
      details: [
        "Venkatesh Prime, 4th-floor",
        "Burudgaon Road",
        "Ahmednagar, Maharashtra, 414001"
      ]
    },
    {
      icon: FiPhone,
      title: "Call Our Team",
      details: ["+91 963 737 5555"]
    },
    {
      icon: FiMail,
      title: "Email Us",
      details: ["hello@samsnutrihub.com", "support@samsnutrihub.com"]
    }
  ];

  const testimonials = [
    {
      initials: "RP",
      name: "Rahul P.",
      quote: "I was skeptical at first, but after our initial consultation, I knew Sam's team was different. They actually understood my lifestyle and built a nutrition plan that made sense for me.",
      result: "Lost 18kg in 6 months"
    },
    {
      initials: "AP",
      name: "Anjali P.",
      quote: "The team at Sam's Nutri Hub fixed my relationship with food in weeks, not months. My energy levels increased by 50% in the first month alone.",
      result: "Reversed pre-diabetes"
    },
    {
      initials: "VK",
      name: "Vikram K.",
      quote: "What impressed me most was how they thought about the long-term. They didn't just fix my immediate weight issues—they built habits that have lasted for years.",
      result: "Gained 12kg muscle mass"
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F9FFF5] to-[#EAF8EC] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B1320] font-poppins leading-tight">
                The Conversation That Changes Everything
              </h1>
              
              <div className="space-y-6 text-[#4A4A4A] leading-relaxed">
                <p>
                  Your journey to optimal health and wellness begins with a single message.
                </p>
                <p>
                  "I wish I had reached out sooner..." That's what almost every client tells us after we start working together.
                </p>
                <p>
                  They spent months (sometimes years) struggling with fad diets, conflicting nutrition advice, and wellness approaches that just wouldn't fit into their real lives.
                </p>
                <p>
                  Then they reached out. We had a conversation. And everything changed.
                </p>
                <p>
                  Not because we have some magic formula (we don't), but because we took the time to understand their unique body, identify the real obstacles, and build nutrition and wellness systems that actually work for their lifestyle.
                </p>
                <p>
                  That conversation could be happening right now. The only question is: are you ready to have it?
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src="https://i.ibb.co/d0W0cD1k/IMG-6088-converted-1691465676-scaled.jpg" 
                alt="Sam's Nutri Hub team." 
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Happens When You Reach Out */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-16 text-center">
              Here's What Happens When You Reach Out
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 max-w-5xl mx-auto">
              {[
                "We listen. No generic advice or one-size-fits-all solutions. We want to understand your specific health challenges and goals.",
                "We analyze. Our team of nutrition experts will look at your current habits and identify the real bottlenecks in your wellness journey.",
                "We propose. You'll get a clear plan of action with specific recommendations tailored to your body, preferences, and lifestyle.",
                "We deliver value upfront. This might be a free nutrition tip, a quick habit adjustment, or strategic advice you can implement immediately.",
                "You decide. No pressure, no hard sell. If it makes sense to work together, great. If not, you'll still walk away with valuable insights."
              ].map((step, index) => (
                <div key={index} className="flex flex-col items-center text-center">
                  <div className="bg-[#FAD74C] w-12 h-12 rounded-full flex items-center justify-center font-bold text-[#0B1320] mb-6">
                    {index + 1}
                  </div>
                  <p className="text-[#4A4A4A]">{step}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-16 max-w-3xl mx-auto text-center">
              <p className="text-[#4A4A4A] italic">
                The worst that could happen? You spend 30 minutes talking about your health and decide we're not a good fit.
              </p>
              <p className="text-[#4A4A4A] italic mt-4">
                The best that could happen? You finally get the nutrition and wellness approach that works like it's supposed to, and your health transforms as a result.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Options */}
      <section className="bg-[#FFFDF8] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {contactOptions.map((option, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg text-center">
                  <div className="bg-[#2E7D32] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={option.icon} className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0B1320] mb-4 font-poppins">{option.title}</h3>
                  <div className="space-y-2 text-[#4A4A4A]">
                    {option.details.map((detail, idx) => (
                      <p key={idx}>{detail}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8 text-center">
              <p className="text-[#9E9E9E] text-sm">
                Open on business days. Response within 24 hours.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lead Form */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins">
              Start Your Transformation Today
            </h2>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto"
          >
            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="bg-[#1B8D35] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiCheck} className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0B1320] mb-4">Thank You!</h3>
                <p className="text-[#4A4A4A]">
                  Our team will contact you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#0B1320] mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiUser} className="h-5 w-5 text-[#9E9E9E]" />
                    </div>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent transition-colors duration-200 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  {errors.name && (
                    <p className="mt-1 text-red-500 text-sm">{errors.name}</p>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#0B1320] mb-2">
                      Your Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent transition-colors duration-200 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="your.email@example.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-red-500 text-sm">{errors.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-[#0B1320] mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent transition-colors duration-200 ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="+91 98765 43210"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-red-500 text-sm">{errors.phone}</p>
                    )}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="goal" className="block text-sm font-medium text-[#0B1320] mb-2">
                    What's your primary health goal? <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="goal"
                    name="goal"
                    value={formData.goal}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent transition-colors duration-200 ${
                      errors.goal ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a goal</option>
                    <option value="fat-loss">Fat Loss</option>
                    <option value="weight-gain">Weight Gain</option>
                    <option value="medical-management">Medical Management</option>
                    <option value="performance">Performance</option>
                    <option value="general-wellness">General Wellness</option>
                  </select>
                  {errors.goal && (
                    <p className="mt-1 text-red-500 text-sm">{errors.goal}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="journey" className="block text-sm font-medium text-[#0B1320] mb-2">
                    Tell us about your health journey so far <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3">
                      <SafeIcon icon={FiMessageSquare} className="h-5 w-5 text-[#9E9E9E]" />
                    </div>
                    <textarea
                      id="journey"
                      name="journey"
                      value={formData.journey}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent transition-colors duration-200 ${
                        errors.journey ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="What have you tried before? What challenges are you facing? What would success look like for you?"
                    ></textarea>
                  </div>
                  <p className="mt-1 text-sm text-[#9E9E9E]">
                    What have you tried before? What challenges are you facing? What would success look like for you?
                  </p>
                  {errors.journey && (
                    <p className="mt-1 text-red-500 text-sm">{errors.journey}</p>
                  )}
                </div>
                
                <div className="flex items-center">
                  <input
                    id="consent"
                    type="checkbox"
                    className="h-4 w-4 text-[#2E7D32] focus:ring-[#2E7D32] border-gray-300 rounded"
                    required
                  />
                  <label htmlFor="consent" className="ml-2 block text-sm text-[#4A4A4A]">
                    I agree to be contacted as per the <Link to="/privacy-policy" className="text-[#2E7D32] hover:underline">Privacy Policy</Link>. <span className="text-red-500">*</span>
                  </label>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-[#2E7D32] text-white py-4 px-6 rounded-lg font-semibold hover:bg-[#205C25] transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <span>Start My Transformation Journey</span>
                  </button>
                  <Link
                    to="/plans"
                    className="flex-1 border-2 border-[#2E7D32] text-[#2E7D32] py-4 px-6 rounded-lg font-semibold hover:bg-[#2E7D32] hover:text-white transition-colors duration-200 text-center"
                  >
                    See Our Plans
                  </Link>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* The 24-Hour Promise */}
      <section className="bg-[#EAF8EC] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-6">
              The 24-Hour Promise
            </h2>
            <p className="text-xl text-[#4A4A4A] max-w-3xl mx-auto">
              When you reach out to us, we promise to respond within 24 hours on business days. No automated replies, no generic responses—just real conversations that move your health forward.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-16 text-center">
              What Our Clients Say About Their Transformation
            </h2>
            
            <motion.div 
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-[#F9FFF5] p-8 rounded-xl"
                >
                  <div className="flex items-center mb-6">
                    <div className="bg-[#2E7D32] w-12 h-12 rounded-full flex items-center justify-center text-white font-bold mr-4">
                      {testimonial.initials}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0B1320]">{testimonial.name}</h3>
                      <p className="text-sm text-[#2E7D32] font-medium">{testimonial.result}</p>
                    </div>
                  </div>
                  <p className="text-[#4A4A4A] italic">"{testimonial.quote}"</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final Step */}
      <section className="bg-[#205C25] py-20 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold text-white font-poppins mb-6">
              The Next Step Is Simple
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Fill out the form above, and let's start the conversation that could transform your health. Remember: The only regret our clients have is not reaching out sooner.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#start-form" 
                className="bg-white text-[#205C25] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                Start My Transformation Journey
              </a>
              <Link
                to="/body-analysis"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#205C25] transition-colors duration-200"
              >
                Start Your Free Assessment
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;